/**
 * A total result type. The domain and application layers never throw for
 * expected outcomes — "the range is invalid", "the sub area is at its cap",
 * "you lack that permission" are all values, not exceptions. That keeps the
 * error paths in the type signature where the caller cannot forget them.
 *
 * Exceptions are reserved for genuine bugs (a violated invariant) and for
 * infrastructure faults, which the outer layers translate into a Result.
 */

export type Result<T, E> = Ok<T> | Err<E>;

export interface Ok<T> {
  readonly ok: true;
  readonly value: T;
}

export interface Err<E> {
  readonly ok: false;
  readonly error: E;
}

export const ok = <T>(value: T): Ok<T> => ({ ok: true, value });

export const err = <E>(error: E): Err<E> => ({ ok: false, error });

export const isOk = <T, E>(r: Result<T, E>): r is Ok<T> => r.ok;

export const isErr = <T, E>(r: Result<T, E>): r is Err<E> => !r.ok;

/** Applies `fn` to a success value, leaving a failure untouched. */
export const mapResult = <T, U, E>(r: Result<T, E>, fn: (value: T) => U): Result<U, E> =>
  r.ok ? ok(fn(r.value)) : r;

/** Applies `fn` to a failure, leaving a success untouched. */
export const mapErr = <T, E, F>(r: Result<T, E>, fn: (error: E) => F): Result<T, F> =>
  r.ok ? r : err(fn(r.error));

/** Chains a fallible step. */
export const andThen = <T, U, E>(
  r: Result<T, E>,
  fn: (value: T) => Result<U, E>,
): Result<U, E> => (r.ok ? fn(r.value) : r);

/**
 * Collects a list of results into a result of a list, failing on the first
 * error. Used when decoding a page of documents where any bad document should
 * abort the batch — note that the Firestore codecs deliberately do *not* use
 * this: there, one unreadable document must not blank an entire screen.
 */
export const allResults = <T, E>(results: readonly Result<T, E>[]): Result<T[], E> => {
  const values: T[] = [];
  for (const r of results) {
    if (!r.ok) return r;
    values.push(r.value);
  }
  return ok(values);
};

/** Unwraps a success or falls back. Never throws. */
export const unwrapOr = <T, E>(r: Result<T, E>, fallback: T): T => (r.ok ? r.value : fallback);

/**
 * Unwraps a success or throws. Only for tests and for the narrow case of a
 * literal known to be valid at the call site (e.g. a hard-coded seed date).
 */
export const unwrapOrThrow = <T, E>(r: Result<T, E>): T => {
  if (r.ok) return r.value;
  const detail =
    typeof r.error === 'object' && r.error !== null && 'message' in r.error
      ? String((r.error as { message: unknown }).message)
      : JSON.stringify(r.error);
  throw new Error(`unwrapOrThrow on an error result: ${detail}`);
};
